/**
 * A SUB SEAT IS ACCESS, NOT A WEEKLY CLASS. One file, read by both sides of
 * the app: the screens import it, and netlify/functions imports the same file
 * (the way lib/subClassGrant.mjs already imports ./jrStaffAliases.ts), so the
 * schedule a phone subscribes to and the schedule a screen draws cannot drift.
 *
 * Feedback gxpVbdu6, 2026-10-05, from a front-desk staff member: a staff
 * member "did accept a sub request for that class for 10/17 but it's on her
 * calendar every Monday now."
 *
 * WHY THAT HAPPENED. Bobbie's sub window (DECISIONS.md, "A sub claim opens the
 * class to the claimer from pickup until 24 hours after the class", PR #1042)
 * is built by putting the claimer on the class's REAL teaching team for the
 * window: netlify/functions/lib/subClassGrant.mjs writes her uid into
 * coTeacherUids and records it in offerings.subSeats, and the hourly sweep
 * takes it off again. That is deliberate and it stays: every rule and screen
 * that asks "is she a teacher of this class" must hear yes for the window, so
 * the class page, Teach, attendance, notes, the board and media all open.
 *
 * But the SCHEDULE surfaces asked the same question to mean something else,
 * "does this class belong on her week", and a temporary co-teacher answered
 * yes for every week of the window. So:
 *
 *   ACCESS asks isOfferingTeacher and keeps the seat. Untouched.
 *   SCHEDULE strips the temporary seats first (withoutTemporarySubSeats), and
 *   adds back only the date she is actually covering (coveredClassDateKeys),
 *   read off the claimed sub request itself.
 *
 * WHO COUNTS AS TEMPORARY. Exactly the uids offerings.subSeats names, which is
 * the ledger the claim lane writes and the reconcile reads ("a uid NOT in
 * subSeats is a real teacher and is never touched"). Three people are never
 * demoted by it:
 *   - the lead (teacherUid). No lane seats the lead, so an entry for her is
 *     stray data, and a stray entry must not take her own class off her week;
 *   - an assistant. assistantUids is not touched here, so a Jr Staff who
 *     assists a class weekly keeps it weekly while she also covers it once;
 *   - a teacher the Director kept on. promoteOfferingSubSeat drops the ledger
 *     entry, and from then on she is simply a co-teacher.
 * A catalog save that happens to resend coTeacherUids is NOT a promotion
 * (lib/offeringWrite.mjs, subSeatPromotionPatch), so the ledger stays the
 * truth even then, and the reconcile still takes the seat off at the end.
 *
 * ONE PERSON, HOWEVER SHE SIGNS IN (DECISIONS 26 and 27). The claim lane seats
 * a linked Jr Staff on BOTH sign-ins with the same request id, and the grant
 * record on the request names both (classAccess.uid and classAccess.aliasUid).
 * Nothing here keys on which sign-in pressed Claim: the weekly class goes from
 * both, and the covered date reaches both.
 *
 * IMPORTS NOTHING, on purpose, so a Netlify function can load it without
 * pulling a screen's dependencies in behind it.
 */

/** The offering fields a schedule reads to tell a sub seat from a teacher. */
export interface SubSeatOfferingFields {
  teacherUid?: string | null;
  coTeacherUids?: readonly string[] | null;
  /** The denormalized rules list, rebuilt by the claim lane alongside coTeacherUids. */
  teacherUids?: readonly string[] | null;
  /** uid -> the claims holding that uid's TEMPORARY seat open. */
  subSeats?: Readonly<Record<string, unknown>> | null;
}

/** One stored ledger entry, as every screen reads it. */
export interface SubSeatEntry {
  byRequestIds: string[];
}

function cleanUid(value: unknown): string {
  return typeof value === 'string' ? value.trim() : '';
}

function isPlainMap(value: unknown): value is Record<string, unknown> {
  return !!value && typeof value === 'object' && !Array.isArray(value);
}

function requestIdsOf(entry: unknown): string[] {
  const ids = isPlainMap(entry) ? entry.byRequestIds : null;
  return Array.isArray(ids)
    ? ids.filter((id): id is string => typeof id === 'string' && id.length > 0)
    : [];
}

/**
 * The ledger as a screen should hold it: trimmed uid keys, request ids only.
 * addedAt is dropped because nothing on a screen reads it and it arrives as a
 * Firestore Timestamp, which is exactly the kind of field CLAUDE.md says to
 * guard rather than carry. Undefined when there is no seat at all, so a class
 * with no ledger reads exactly as it did before this existed.
 */
export function normalizeSubSeats(value: unknown): Record<string, SubSeatEntry> | undefined {
  if (!isPlainMap(value)) return undefined;
  const out: Record<string, SubSeatEntry> = {};
  for (const [uid, entry] of Object.entries(value)) {
    const clean = cleanUid(uid);
    if (!clean) continue;
    out[clean] = { byRequestIds: requestIdsOf(entry) };
  }
  return Object.keys(out).length > 0 ? out : undefined;
}

/** Every uid holding a TEMPORARY seat on this class. Never the lead. */
export function temporarySubSeatUids(
  offering: SubSeatOfferingFields | null | undefined
): Set<string> {
  const temporary = new Set<string>();
  const seats = offering?.subSeats;
  if (!isPlainMap(seats)) return temporary;
  const lead = cleanUid(offering?.teacherUid);
  for (const uid of Object.keys(seats)) {
    const clean = cleanUid(uid);
    if (clean && clean !== lead) temporary.add(clean);
  }
  return temporary;
}

/**
 * THE CLASS AS A WEEKLY SCHEDULE SEES IT: the same document with every
 * temporary seat taken off coTeacherUids and teacherUids. Hand the result to
 * the predicate a schedule already uses (isOfferingTeacher, offeringStaffUids,
 * jrStaffRoleOnOffering) and it answers for the weekly team. The input is
 * never mutated, and a class with no temporary seat comes back as the very
 * same object, so a schedule pays nothing for a class nobody is covering.
 */
export function withoutTemporarySubSeats<T extends SubSeatOfferingFields>(offering: T): T;
export function withoutTemporarySubSeats<T extends SubSeatOfferingFields>(
  offering: T | null | undefined
): T | null | undefined;
export function withoutTemporarySubSeats<T extends SubSeatOfferingFields>(
  offering: T | null | undefined
): T | null | undefined {
  if (!offering) return offering;
  const temporary = temporarySubSeatUids(offering);
  if (temporary.size === 0) return offering;
  const keep = (list: readonly string[] | null | undefined) =>
    Array.isArray(list) ? list.filter((uid) => !temporary.has(cleanUid(uid))) : list;
  return {
    ...offering,
    coTeacherUids: keep(offering.coTeacherUids),
    teacherUids: keep(offering.teacherUids),
  } as T;
}

/**
 * The claims behind these uids' temporary seats on this class, deduped. This
 * is how a server reader finds the ONE request a seat stands for without
 * scanning the sub board: the seat already names it.
 */
export function subSeatRequestIds(
  offering: SubSeatOfferingFields | null | undefined,
  uids: readonly string[]
): string[] {
  const seats = offering?.subSeats;
  if (!isPlainMap(seats)) return [];
  const temporary = temporarySubSeatUids(offering);
  const mine = new Set(uids.map(cleanUid).filter((uid) => temporary.has(uid)));
  const ids = new Set<string>();
  for (const [uid, entry] of Object.entries(seats)) {
    if (!mine.has(cleanUid(uid))) continue;
    for (const id of requestIdsOf(entry)) ids.add(id);
  }
  return [...ids].sort();
}

/** The fields of a sub request the covered-date lane reads. */
export interface CoverableSubRequest {
  offeringId?: string | null;
  /** The studio date (YYYY-MM-DD, America/Chicago) the request covers. */
  date?: string | null;
  status?: string | null;
  claimedBy?: string | null;
  classAccess?: {
    uid?: unknown;
    aliasUid?: unknown;
    claimedByUid?: unknown;
  } | null;
}

/**
 * Is one of these sign-ins covering this request?
 *
 * claimedBy is the uid that pressed Claim. The grant record also names the
 * seat (classAccess.uid, her canonical teaching uid) and her other sign-in
 * (classAccess.aliasUid), so a Jr Staff who claimed on her dancer sign-in is
 * covering it on her email sign-in too. Only a CLAIMED request counts: an open
 * one is nobody's yet, and a cancelled one is nobody's any more.
 */
export function subRequestCoveredBy(
  request: CoverableSubRequest | null | undefined,
  uids: readonly string[]
): boolean {
  if (!request || request.status !== 'claimed') return false;
  const mine = new Set(uids.map(cleanUid).filter(Boolean));
  if (mine.size === 0) return false;
  const access = isPlainMap(request.classAccess) ? request.classAccess : null;
  const coverUids = [
    request.claimedBy,
    access?.uid,
    access?.aliasUid,
    access?.claimedByUid,
  ].map(cleanUid).filter(Boolean);
  return coverUids.some((uid) => mine.has(uid));
}

/** One class on one studio date. The same key Calendar.tsx's Sub pill uses. */
export function coveredClassDateKey(offeringId: string, dateKey: string): string {
  return `${offeringId}_${dateKey}`;
}

/** Every class-and-date these sign-ins are covering, as coveredClassDateKey keys. */
export function coveredClassDateKeys(
  requests: readonly CoverableSubRequest[],
  uids: readonly string[]
): Set<string> {
  const keys = new Set<string>();
  for (const request of requests) {
    const offeringId = cleanUid(request?.offeringId);
    const date = cleanUid(request?.date);
    if (!offeringId || !date) continue;
    if (!subRequestCoveredBy(request, uids)) continue;
    keys.add(coveredClassDateKey(offeringId, date));
  }
  return keys;
}

/**
 * The studio dates these sign-ins are covering, per class, sorted. Dates
 * before `fromDateKey` (a studio YYYY-MM-DD) are left out when it is given, so
 * a list that shows what is COMING never shows a cover that already happened.
 * The staff home's My classes and the dancer sign-in's class list both read
 * their covered dates through this, which is what keeps her two lists equal.
 */
export function coveredClassDatesByOffering(
  requests: readonly CoverableSubRequest[],
  uids: readonly string[],
  fromDateKey?: string
): Map<string, string[]> {
  const byOffering = new Map<string, Set<string>>();
  for (const request of requests) {
    const offeringId = cleanUid(request?.offeringId);
    const date = cleanUid(request?.date);
    if (!offeringId || !date) continue;
    if (fromDateKey && date < fromDateKey) continue;
    if (!subRequestCoveredBy(request, uids)) continue;
    const dates = byOffering.get(offeringId) ?? new Set<string>();
    dates.add(date);
    byOffering.set(offeringId, dates);
  }
  return new Map([...byOffering].map(([id, dates]) => [id, [...dates].sort()]));
}

/** The classes these sign-ins are covering on ONE studio date. */
export function coveredOfferingIdsOn(
  requests: readonly CoverableSubRequest[],
  uids: readonly string[],
  dateKey: string
): Set<string> {
  const ids = new Set<string>();
  for (const request of requests) {
    if (cleanUid(request?.date) !== dateKey) continue;
    const offeringId = cleanUid(request?.offeringId);
    if (offeringId && subRequestCoveredBy(request, uids)) ids.add(offeringId);
  }
  return ids;
}

/**
 * THE TEMPORARY SEATS THAT ARE COVERING THIS CLASS ON ONE STUDIO DATE, in the
 * class's own team order (coTeacherUids), so a caller that keeps "the first
 * seat of one person" keeps the same one every time.
 *
 * Bobbie's rulings of 2026-10-07 (DECISIONS 123, items 5 and 6): the sub's
 * name shows "only on that day", and the Take attendance reminder goes to the
 * sub "only the night she covers". A seat is a cover on a date when a CLAIMED
 * request for THIS class on THAT date names the seat (subRequestCoveredBy,
 * which reads claimedBy and the grant's own uid pair, so a Jr Staff covers on
 * both sign-ins). A seat whose claims are all for other dates answers no, and
 * the weekly team is never asked about here at all.
 *
 * `offeringId` is the class's document id. It is passed rather than read off
 * the offering because a stored Firestore document does not carry its own id.
 */
export function subSeatUidsCoveringOn(
  offering: SubSeatOfferingFields | null | undefined,
  offeringId: string,
  requests: readonly CoverableSubRequest[],
  dateKey: string,
): string[] {
  const temporary = temporarySubSeatUids(offering);
  const classId = cleanUid(offeringId);
  const day = cleanUid(dateKey);
  if (temporary.size === 0 || !classId || !day) return [];
  const onThisNight = requests.filter((request) => cleanUid(request?.offeringId) === classId
    && cleanUid(request?.date) === day);
  if (onThisNight.length === 0) return [];
  const covering = (uid: string) => onThisNight.some((request) => subRequestCoveredBy(request, [uid]));
  const ordered = Array.isArray(offering?.coTeacherUids)
    ? offering.coTeacherUids.map(cleanUid).filter((uid) => temporary.has(uid))
    : [];
  const seen = new Set<string>();
  const out: string[] = [];
  for (const uid of [...ordered, ...[...temporary].sort()]) {
    if (seen.has(uid)) continue;
    seen.add(uid);
    if (covering(uid)) out.push(uid);
  }
  return out;
}

/**
 * THE CLASS AS ONE STUDIO DATE SEES IT: the weekly team, plus exactly the
 * temporary seats named in `coveringUids` (subSeatUidsCoveringOn for that
 * date). Every other temporary seat is taken off coTeacherUids and
 * teacherUids, the same way withoutTemporarySubSeats takes them all off.
 *
 * So the predicate a dated surface already uses (offeringTeacherUids, the
 * attendance reminder's teacherUidsForOffering) answers "who teaches it on
 * this date": the regular team every week, the sub on the night she covers.
 * The input is never mutated, and a class with no temporary seat comes back as
 * the same object.
 */
export function withSubSeatsCoveringOn<T extends SubSeatOfferingFields>(
  offering: T,
  coveringUids: Iterable<string>,
): T;
export function withSubSeatsCoveringOn<T extends SubSeatOfferingFields>(
  offering: T | null | undefined,
  coveringUids: Iterable<string>,
): T | null | undefined;
export function withSubSeatsCoveringOn<T extends SubSeatOfferingFields>(
  offering: T | null | undefined,
  coveringUids: Iterable<string>,
): T | null | undefined {
  if (!offering) return offering;
  const temporary = temporarySubSeatUids(offering);
  if (temporary.size === 0) return offering;
  const keepSeat = new Set([...coveringUids].map(cleanUid).filter(Boolean));
  const drop = new Set([...temporary].filter((uid) => !keepSeat.has(uid)));
  if (drop.size === 0) return offering;
  const keep = (list: readonly string[] | null | undefined) =>
    Array.isArray(list) ? list.filter((uid) => !drop.has(cleanUid(uid))) : list;
  return {
    ...offering,
    coTeacherUids: keep(offering.coTeacherUids),
    teacherUids: keep(offering.teacherUids),
  } as T;
}
